import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  DatePicker,
  Space,
  message,
  Empty,
} from 'antd';
import { fetchSalesOverview } from '../../api/stats';

const { RangePicker } = DatePicker;

const SalesProfitReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(29, 'day'),
    dayjs(),
  ]);

  const loadData = async () => {
    if (!dateRange || dateRange.length !== 2) return;
    setLoading(true);
    try {
      const res = await fetchSalesOverview({
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
      });
      if (res.success) {
        setData(res.data);
      } else {
        message.error(res.message || '获取销售报表失败');
      }
    } catch (error) {
      message.error('获取销售报表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  return (
    <div className="workspace-page">
      <div className="workspace-header">
        <div>
          <h1 className="workspace-title">销售与毛利报表</h1>
          <p className="workspace-description">销售额、退款、成本与毛利分析</p>
        </div>
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          allowClear={false}
        />
      </div>

      {/* 核心指标 */}
      <Row gutter={[16, 16]} className="workspace-summary">
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="销售额（实付）"
              value={data?.paid_amount || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#23855B' }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              订单数 {data?.paid_orders || 0}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="退款金额"
              value={data?.refund_amount || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#fa541c' }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              退款订单 {data?.refund_orders || 0}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="毛利（收入-成本）"
              value={data?.gross_profit || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: data?.gross_profit >= 0 ? '#1890ff' : '#ff4d4f' }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              销售成本 {data?.cost_amount || 0}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="毛利率"
              value={data?.gross_margin || 0}
              precision={2}
              suffix="%"
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              平均客单价 ¥{Number(data?.avg_order_amount || 0).toFixed(2)}
            </div>
          </Card>
        </Col>
      </Row>

      {/* 销售趋势 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card title="销售趋势" size="small">
            {data?.sales_trend && data.sales_trend.length > 0 ? (
              <Table
                rowKey="date"
                size="small"
                pagination={false}
                scroll={{ y: 280 }}
                dataSource={data.sales_trend}
                columns={[
                  { title: '日期', dataIndex: 'date' },
                  {
                    title: '订单数',
                    dataIndex: 'order_count',
                    align: 'right',
                    render: (v) => <Tag color="blue">{v}</Tag>,
                  },
                  {
                    title: '销售额',
                    dataIndex: 'total_amount',
                    align: 'right',
                    render: (v) => <span style={{ color: '#23855B' }}>¥{Number(v).toFixed(2)}</span>,
                  },
                ]}
              />
            ) : (
              <Empty description="该时间段暂无销售数据" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="分类销售 Top" size="small">
            {data?.category_stats && data.category_stats.length > 0 ? (
              <Table
                rowKey={(record, index) => index}
                size="small"
                pagination={false}
                scroll={{ y: 280 }}
                dataSource={data.category_stats}
                columns={[
                  {
                    title: '分类',
                    dataIndex: ['product', 'category', 'name'],
                    render: (_, record) => record.product?.category?.name || '未分类',
                  },
                  {
                    title: '销量',
                    dataIndex: 'total_sales',
                    align: 'right',
                  },
                  {
                    title: '销售额',
                    dataIndex: 'total_revenue',
                    align: 'right',
                    render: (v) => `¥${Number(v).toFixed(2)}`,
                  },
                ]}
              />
            ) : (
              <Empty description="暂无分类数据" />
            )}
          </Card>
        </Col>
      </Row>

      {/* 热销商品 */}
      <Row style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="热销商品 Top10" size="small">
            <Table
              rowKey="product_id"
              size="small"
              pagination={false}
              dataSource={data?.product_ranking || []}
              columns={[
                {
                  title: '商品',
                  render: (_, record) => (
                    <Space>
                      {record.product?.main_image && (
                        <img
                          src={record.product.main_image}
                          alt={record.product.name}
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <span>{record.product?.name || '-'}</span>
                    </Space>
                  ),
                },
                {
                  title: '销量',
                  dataIndex: 'total_sales',
                  align: 'right',
                  render: (v) => <Tag color="green">{v}</Tag>,
                },
                {
                  title: '销售额',
                  dataIndex: 'total_revenue',
                  align: 'right',
                  render: (v) => <span style={{ fontWeight: 'bold' }}>¥{Number(v).toFixed(2)}</span>,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SalesProfitReport;
